import { Divider } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import capitalize from "lodash/capitalize";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useIsFeatureEnabled } from "#app/featureToggle";
import { translateDomainError } from "#app/i18n/translateApiError";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { useCompany } from "#modules/company/view/useCompany";
import { getMaxNumberOfItemsPerPanel } from "#modules/layouts/domain/getMaxNumberOfItemsPerPanel";
import { LayoutNesting } from "#modules/layouts/infra/LayoutNesting.dto";
import { LayoutCanvas } from "#modules/layouts/view/LayoutCanvas";
import { NestingRunViewer } from "#modules/layouts/view/NestingRunViewer";
import { useRunLayoutNesting } from "#modules/layouts/view/useRunLayoutNesting";
import { useUpdateLayoutNesting } from "#modules/layouts/view/useUpdateLayoutNesting";
import { useUpdateMaterialLayoutNesting } from "#modules/materialLayouts/view/useUpdateMaterialLayoutNesting";
import { getNestingLabel } from "#modules/quoteRequestLines/helpers/getNestingLabel";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { Alert } from "#shared/view/components/Alert";
import { Flex } from "#shared/view/components/Flex";
import { PADDING_HORIZONTAL, SectionWrapper } from "#shared/view/components/SectionWrapper";
import { Space } from "#shared/view/components/Space";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { Checkbox } from "#shared/view/designSystem/Checkbox";
import { ComputingResult } from "#shared/view/designSystem/ComputingResult";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { RecommendedNumberInputController } from "#shared/view/designSystem/inputs/RecommendedNumberInputController";
import { ScrollArea } from "#shared/view/designSystem/ScrollArea";
import { Tabs } from "#shared/view/designSystem/Tabs";
import { Features } from "#types/FeatureToggleTypes";
import {
  computeNeededQuantity,
  formatPercentage,
  getSkeletonLoss,
  isDomainError,
  Layout,
  PartTypeSchema,
} from "shared";

import { LayoutDto } from "../infra/fetchLayout.api";
import { layoutQuery } from "../infra/layout.query";
import { useUpdateLayout } from "./useUpdateLayout";

type Props = {
  isLayoutComputed: boolean;
  setIsLayoutComputed: Dispatch<SetStateAction<boolean>>;
  handleSetCurrentLayoutNesting?: (layoutNesting: LayoutNesting) => void;
  currentLayoutNesting?: LayoutNesting | null;
  layout: LayoutDto;
  quoteRequestLineId: string;
};

const CANVAS_HEIGHT = 600;
const THREE_SECONDS = 3 * 1000;

export const CanvasSection = ({
  isLayoutComputed,
  setIsLayoutComputed,
  handleSetCurrentLayoutNesting,
  currentLayoutNesting,
  layout,
  quoteRequestLineId,
}: Props) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const { company } = useCompany();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [trueShapeErrorMessage, setTrueShapeErrorMessage] = useState<string | null>(null);
  const { control, getValues, setValue } = useFormContext<QuoteRequestLineFormData>();
  const [activeTab, setActiveTab] = useState<string | null>(
    currentLayoutNesting?.panels[0]?.name ?? null,
  );
  const [recommendedScrapRatioBillingRate, setRecommendedScrapRatioBillingRate] = useState<
    number | undefined
  >(undefined);
  const [highligthMaterialQuantity, setHighligthMaterialQuantity] = useState(false);

  const maxWidth = (containerRef.current?.clientWidth || CANVAS_HEIGHT) - PADDING_HORIZONTAL * 2;

  const onLayoutNestingSuccess = useCallback(
    (data: { layoutNesting: LayoutNesting | undefined; maxNumberOfItemsPerPanel: number }) => {
      if (!data.layoutNesting) {
        return;
      }

      void queryClient.invalidateQueries(quoteRequestLineQuery({ id: quoteRequestLineId }));
      const layoutNesting = data.layoutNesting;

      handleSetCurrentLayoutNesting?.(layoutNesting);

      if (layoutNesting.scrapRatioBillingRate) {
        setRecommendedScrapRatioBillingRate(layoutNesting.scrapRatioBillingRate);
      }

      setActiveTab(layoutNesting.panels[0]?.name ?? null);

      setIsLayoutComputed(true);
    },
    [setIsLayoutComputed, queryClient, quoteRequestLineId, handleSetCurrentLayoutNesting],
  );

  const {
    updateLayoutNesting,
    isPending: isBaseLoading,
    data = {
      isError: false,
      layoutNesting: currentLayoutNesting,
      maxNumberOfItemsPerPanel: currentLayoutNesting
        ? getMaxNumberOfItemsPerPanel({
            layoutNesting: currentLayoutNesting,
          })
        : 0,
    },
  } = useUpdateLayoutNesting(layout.id, {
    onSuccess: onLayoutNestingSuccess,
    onError: (error) => {
      setIsLayoutComputed(true);

      if (error instanceof AxiosError && error.code === "ECONNABORTED") {
        setErrorMessage(t("layout.errors.nestingTimeout"));
      } else if (isDomainError(error)) {
        setErrorMessage(translateDomainError(error));
      } else {
        setErrorMessage(t("layout.errors.nestingError"));
      }
    },
  });

  const part = useWatch({ control, name: "part" });
  const quantity = useWatch({ control, name: "quantity" });
  const reworkRatio = useWatch({ control, name: "reworkRatio" });
  const watchedLayout = useWatch({ control, name: "layout" });

  const skeletonWidth = watchedLayout?.skeletonWidth ?? layout.skeletonWidth ?? 0;
  const cutWidth = watchedLayout?.cutWidth ?? layout.cutWidth ?? 0;

  const partType = part?.type;
  const isSheetPart = partType === PartTypeSchema.enum.SHEET;
  const isTrueShapeNestingEnabled = useIsFeatureEnabled(Features.TRUE_SHAPE_NESTING);
  const isTrueShapeNesting = isSheetPart && isTrueShapeNestingEnabled;
  const nestingLabel = getNestingLabel(partType);
  const capitalizedNestingLabel = capitalize(nestingLabel);

  const { runLayoutNesting } = useRunLayoutNesting(layout.id, {
    onError: (error) => {
      setTrueShapeErrorMessage(
        isDomainError(error) ? translateDomainError(error) : t("layout.errors.nestingError"),
      );
    },
  });

  const handleLayoutNestingUpdate = () => {
    setErrorMessage(null);
    setTrueShapeErrorMessage(null);

    if (!part) {
      setErrorMessage(t("layout.errors.noPart"));
      return;
    }

    const materialLayout = layout.materialLayout;

    if (!materialLayout) {
      setErrorMessage(t("layout.errors.noMaterialLayout"));
      return;
    }

    if (!materialLayout.length || !materialLayout.width) {
      setErrorMessage(t("layout.errors.noMaterialDimensions"));
      return;
    }

    updateLayoutNesting({
      materialLayoutId: materialLayout.id,
      skeletonWidth,
      cutWidth,
      part: part,
      quantity: computeNeededQuantity({
        quantity: quantity ?? 1,
        reworkRatio: reworkRatio ?? 0,
      }),
    });
    if (isTrueShapeNesting) {
      runLayoutNesting();
    }
  };

  useEffect(() => {
    if (quantity && isLayoutComputed) {
      setHighligthMaterialQuantity(true);
      setTimeout(() => {
        setHighligthMaterialQuantity(false);
      }, THREE_SECONDS);
    }
  }, [quantity, isLayoutComputed]);

  const defaultData = data.layoutNesting;

  const defaultTabsValue =
    defaultData?.panels && defaultData.panels[0] && defaultData.panels[0].name;

  const materialLayout = layout.materialLayout;
  const canComputeLayout = materialLayout?.length && materialLayout.width;
  const hasError = data.layoutNesting?.error || errorMessage;

  const { updateLayout } = useUpdateLayout(quoteRequestLineId);
  const { updateMaterialLayoutNesting } = useUpdateMaterialLayoutNesting({
    quoteRequestLineId,
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(layoutQuery(materialLayout?.layoutId));
      },
    },
  });

  const hasAutoScrapRatio = watchedLayout?.hasAutoScrapRatio;

  const handleLayoutOnBlur = (key: keyof Layout) => () => {
    const value = getValues(`layout.${key}`);

    analytics.capture(AnalyticsEvent.DetailsPanelLayoutEdited, {
      quote_request_line_id: quoteRequestLineId,
      field: key,
    });

    updateLayout({ id: layout.id, payload: { [key]: value, quoteRequestLineId } });
  };

  const updateMaterialLayoutMaxPartsPerLayout = (value: number) => {
    analytics.capture(AnalyticsEvent.DetailsPanelLayoutEdited, {
      quote_request_line_id: quoteRequestLineId,
      field: "maxPartsPerLayout",
    });

    updateMaterialLayoutNesting({
      id: materialLayout?.id,
      payload: { maxPartsPerLayout: value },
    });
  };

  const hasNestingResult = !isBaseLoading && !!data.layoutNesting;

  const activePanel = data.layoutNesting?.panels.find((p) => p.name === activeTab);

  const nestingSkeletonWidth = data.layoutNesting?.skeletonWidth ?? 0;
  const skeletonLoss = partType
    ? getSkeletonLoss(partType, nestingSkeletonWidth)
    : nestingSkeletonWidth;
  const activePanelSheetArea = activePanel
    ? (activePanel.width + skeletonLoss) * (activePanel.height + skeletonLoss)
    : 0;
  const activePanelArea = activePanel ? activePanel.width * activePanel.height : 0;
  const activePanelItemsArea =
    data.layoutNesting?.items
      .filter((i) => i.panel === activeTab)
      .reduce((sum, i) => sum + i.width * i.height, 0) ?? 0;
  const activePanelUsedArea =
    data.layoutNesting?.items
      .filter((i) => i.panel === activeTab && i.type === "used")
      .reduce((sum, i) => sum + i.width * i.height, 0) ?? 0;
  const fullScrapRatio =
    activePanelSheetArea > 0
      ? ((activePanelSheetArea - activePanelUsedArea) / activePanelSheetArea) * 100
      : 0;
  const skeletonPercentage =
    activePanelSheetArea > 0
      ? ((activePanelSheetArea - activePanelArea) / activePanelSheetArea) * 100
      : 0;
  const cutsPercentage =
    activePanelSheetArea > 0
      ? ((activePanelArea - activePanelItemsArea) / activePanelSheetArea) * 100
      : 0;

  const handleToggleManualScrap = (checked: boolean) => {
    setValue("layout.hasAutoScrapRatio", !checked);

    analytics.capture(AnalyticsEvent.DetailsPanelLayoutEdited, {
      quote_request_line_id: quoteRequestLineId,
      field: "hasAutoScrapRatio",
    });

    updateLayout({
      id: layout.id,
      payload: {
        hasAutoScrapRatio: !checked,
        quoteRequestLineId,
        scrapRatio: checked ? fullScrapRatio : 0,
      },
    });
    if (checked) {
      setValue("layout.scrapRatio", fullScrapRatio);
    }
  };

  return (
    <SectionWrapper ref={containerRef} color="blue">
      <Stack gap="xs">
        <Flex gap="xs" align="end">
          <RecommendedNumberInputController
            control={control}
            name="layout.cutWidth"
            label={t("material.cutWidth")}
            unit="mm"
            onBlur={handleLayoutOnBlur("cutWidth")}
          />
          <RecommendedNumberInputController
            control={control}
            name="layout.skeletonWidth"
            label={t("layout.skeletonWidth")}
            unit="mm"
            onBlur={handleLayoutOnBlur("skeletonWidth")}
          />
        </Flex>

        <Checkbox
          label={t("layout.scrap.customize")}
          description={t("layout.scrap.customizeDescription")}
          checked={!hasAutoScrapRatio}
          onChange={(e) => handleToggleManualScrap(e.currentTarget.checked)}
        />

        {!hasAutoScrapRatio && (
          <Flex gap="xs" align="end">
            <RecommendedNumberInputController
              control={control}
              name="layout.scrapRatio"
              label={t("layout.scrap.ratioLabel")}
              unit="%"
              onBlur={handleLayoutOnBlur("scrapRatio")}
              computedValue={{
                value: fullScrapRatio,
                message: t("layout.scrap.computedByNesting"),
              }}
            />
            <RecommendedNumberInputController
              control={control}
              name="layout.scrapMaterialBillingRate"
              label={t("material.scrapMaterialBillingRate")}
              unit="%"
              onBlur={handleLayoutOnBlur("scrapMaterialBillingRate")}
              companyValue={{
                value: company?.companySettings.scrapMaterialBillingRate,
                message: t("layout.scrap.companyDefaultRate"),
              }}
              computedValue={
                recommendedScrapRatioBillingRate
                  ? {
                      value: recommendedScrapRatioBillingRate,
                      message: t("layout.scrap.recommendedByFabera"),
                    }
                  : undefined
              }
            />
          </Flex>
        )}

        <Button
          onClick={handleLayoutNestingUpdate}
          loading={isBaseLoading}
          disabled={!canComputeLayout}
          style={{ alignSelf: "flex-start" }}
        >
          {t("layout.computeNesting")}
        </Button>
      </Stack>

      {hasNestingResult && (
        <>
          <Divider my="xs" />
          <Text fz={fontSizes.small} c="dimmed" fw={600} mb="xs">
            {t("layout.results")}
          </Text>
          <Flex align="flex-end" gap="xs">
            <NumberInputController
              name="layout.materialLayout.quantity"
              label={`${t("layout.quantityRequired", { material: nestingLabel })}`}
              control={control}
              highlight={highligthMaterialQuantity}
              disabled
              precision={3}
            />
            {!!materialLayout?.maxPartsPerLayout && materialLayout.maxPartsPerLayout > 0 && (
              <NumberInputController
                name="layout.materialLayout.maxPartsPerLayout"
                label={`${t("layout.maxPartsPerMaterial", {
                  material: nestingLabel,
                })}`}
                control={control}
                highlight={highligthMaterialQuantity}
                onBlur={(e) =>
                  updateMaterialLayoutMaxPartsPerLayout(parseInt(e.currentTarget.value))
                }
                precision={3}
              />
            )}
            {hasAutoScrapRatio && !!data.layoutNesting && (
              <ComputingResult
                label={t("layout.scrap.ratioLabel")}
                value={formatPercentage(fullScrapRatio)}
              />
            )}
          </Flex>
        </>
      )}

      <Space h="xs" />

      {!isBaseLoading && hasError && (
        <Alert color="red">{data.layoutNesting?.error || errorMessage}</Alert>
      )}
      {!isBaseLoading && !!data.layoutNesting?.unusedArea && !hasError && (
        <Text fz={fontSizes.small} mb="xs">
          {t("layout.unusedAreaDescription")}
        </Text>
      )}

      {!isBaseLoading && data.layoutNesting && !hasError && (
        <Flex gap="xs" align="end" mb="xs">
          <ComputingResult
            label={t("material.cutWidth")}
            value={formatPercentage(cutsPercentage)}
          />
          <ComputingResult
            label={t("layout.skeletonWidth")}
            value={formatPercentage(skeletonPercentage)}
          />
        </Flex>
      )}

      {isTrueShapeNesting && (
        <NestingRunViewer layoutId={layout.id} errorMessage={trueShapeErrorMessage} />
      )}

      {!isTrueShapeNesting && !isBaseLoading && data.layoutNesting && !hasError && (
        <Tabs defaultValue={defaultTabsValue} onChange={setActiveTab} value={activeTab}>
          <Tabs.List>
            <ScrollArea offsetScrollbars>
              <Flex>
                {data.layoutNesting.panels.map((panel, index) => {
                  const filteredItems =
                    data.layoutNesting?.items.filter(
                      (item) => item.panel === panel.name && item.type === "used",
                    ) ?? [];

                  return (
                    <Tabs.Tab key={panel.name} value={panel.name}>
                      {t("layout.materialNumber", {
                        material: capitalizedNestingLabel,
                        number: index + 1,
                        count: filteredItems.length,
                      })}
                    </Tabs.Tab>
                  );
                })}
              </Flex>
            </ScrollArea>
          </Tabs.List>

          <Space h="xs" />

          {data.layoutNesting.panels.map((panel) => (
            <Tabs.Panel key={panel.name} value={panel.name}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <LayoutCanvas
                  panelId={panel.name}
                  data={data.layoutNesting ?? undefined}
                  width={maxWidth}
                  height={CANVAS_HEIGHT}
                />
              </div>
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </SectionWrapper>
  );
};
